import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiService } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Eye, EyeClosed } from "lucide-react";

const LoginForm = () => {
  const { dispatch } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Sending login request...");
      const response = await apiService.post("/api/auth/login", null, { email, password });
  
      console.log("Login response:", response.data);
      if (response.data?.accessToken) {
        dispatch({
          type: "LOGIN",
          payload: {
            token: response.data.accessToken,
            refreshToken: response.data.refreshToken,
            user: { id: response.data.id } 
          }
        });
        navigate("/dashboard");
      } else {
        console.error("No token received!!!");
        setError("Login failed. Please try again.");
      }
    } catch (err) {
      console.error("Login error:", err.response?.data || err.message);
      if (err.response) {
        // The request was made and the server responded with a status code
        switch (err.response.status) {
          case 400:
            setError("Invalid login credentials. Please check your email and password.");
            break;
          case 401:
            setError("Unauthorized. The email or password is incorrect.");
            break;
          case 403:
            setError("Access forbidden. Your account may be locked or disabled.");
            break;
          case 404:
            setError("Account not found. Please check your email or sign up.");
            break;
          case 500:
            setError("Server error. Our team has been notified. Please try again later.");
            break;
          case 503:
            setError("Service unavailable. The server is temporarily overloaded or down.");
            break;
          default:
            setError("An unexpected error occurred. Please try again.");
        }
      } else if (err.request) {
        setError("No response from server. Please check your internet connection.");
      } else {
        setError("Error setting up the login request. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="w-full">
      <h1 className="text-2xl text-center font-bold text-gray-800 mb-6">Welcome back</h1>
      
      <form onSubmit={handleLogin} className="space-y-5">
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email address
          </label>
          <div className="relative group">
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:ring-1 focus:ring-primary-600 focus:border-transparent transition-all duration-200 outline-none"
              required
            />
            <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary-600 transition-all duration-200 group-focus-within:w-full"></div>
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label htmlFor="password" className="text-sm font-medium text-gray-700">
              Password
            </label>
            <Link 
              to="/forgot-password" 
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative group">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:ring-1 focus:ring-primary-600 focus:border-transparent transition-all duration-200 outline-none"
              required
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <Eye size={20} /> : <EyeClosed size={20} />}
            </button>
            <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary-600 transition-all duration-200 group-focus-within:w-full"></div>
          </div>
        </div>
        
        <div className="pt-2">
          {error && <p className="text-red-500">{error}</p>}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center items-center py-3 px-4 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 disabled:opacity-70"
          >
            {isLoading ? (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : null}
            {isLoading ? "Signing in..." : "Sign in"}
          </button>
        </div>
        
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Don't have an account?</span>
            </div>
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => navigate("/signup")}
              className="w-full inline-flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Create an account
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;